import React, { useState, useMemo } from "react";
import { formatDateForAPI } from "@/lib/date-utils";
import { useLocation } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import {
  Car,
  Clock,
  MapPin,
  Calendar,
  Eye,
  MoreHorizontal,
  Filter,
  Download,
  Trash2,
  Edit,
  Camera,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ServerPaginatedTable } from "@/components/common/server-paginated-table";
import { GlobalFilterBar } from "@/components/common/global-filter-bar";
import { FuzzyMatchingInfoModal } from "@/components/common/fuzzy-matching-info-modal";
import { sessionAPI } from "@/api";
import { usePaginatedApi, useToastHelpers } from "@/hooks";
import { useDateRange } from "@/contexts/DateRangeContext";
import { LPRSession, SessionFilters } from "@/types";
import {
  formatDateTime,
  formatDuration,
  formatRelativeTime,
  cn,
} from "@/lib/utils";
import { exportSessionsToCSV, generateCSVFilename } from "@/lib/csv-export";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const statusColors = {
  active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  completed: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  orphaned:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  invalid: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

export function Sessions() {
  const location = useLocation();
  const [filters, setFilters] = useState<SessionFilters>({});
  const [selectedRows, setSelectedRows] = useState<LPRSession[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { success, error: showError } = useToastHelpers();
  const { dateRange, selectedLocations } = useDateRange();

  // Apply filter from navigation state (dashboard click)
  React.useEffect(() => {
    const navigationFilter = location.state?.filter;
    if (navigationFilter) {
      const newFilters = {
        ...filters,
        [navigationFilter.key]: navigationFilter.value,
      };
      setFilters(newFilters);

      // Immediately update the API call with the new filter
      updateParams({
        page: 1,
        per_page: pageSize,
        start_date: formatDateForAPI(dateRange.from),
        end_date: formatDateForAPI(dateRange.to),
        ...(selectedLocations.length > 0 && {
          location: selectedLocations.join(","),
        }),
        ...newFilters,
      });

      // Clear navigation state so it doesn't persist
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch sessions with pagination
  const {
    data: sessions,
    pagination,
    isLoading,
    error,
    updateParams,
    refresh,
  } = usePaginatedApi(sessionAPI.getSessions, {
    page: currentPage,
    per_page: pageSize,
    start_date: formatDateForAPI(dateRange.from),
    end_date: formatDateForAPI(dateRange.to),
    ...(selectedLocations.length > 0 && {
      location: selectedLocations.join(","),
    }),
    ...filters,
  });

  // Re-fetch when global filters change
  React.useEffect(() => {
    updateParams({
      page: 1,
      per_page: pageSize,
      start_date: formatDateForAPI(dateRange.from),
      end_date: formatDateForAPI(dateRange.to),
      ...(selectedLocations.length > 0 && {
        location: selectedLocations.join(","),
      }),
      ...filters,
    });
    setCurrentPage(1);
  }, [dateRange, selectedLocations, filters, pageSize]);

  // Define table columns
  const columns: ColumnDef<LPRSession>[] = useMemo(
    () => [
      {
        id: "entry_plate",
        header: "Entry Plate",
        accessorKey: "entry_plate",
        cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <Car className="h-4 w-4 text-muted-foreground" />
            <span className="font-mono font-medium">
              {row.original.entry_plate}
            </span>
          </div>
        ),
      },
      {
        id: "entry_time",
        header: "Entry Time",
        accessorKey: "entry_time",
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-medium">
              {formatDateTime(row.original.entry_time)}
            </div>
            <div className="text-xs text-muted-foreground">
              Cam {row.original.entry_camera}
            </div>
          </div>
        ),
      },
      {
        id: "exit_plate",
        header: "Exit Plate",
        accessorKey: "exit_plate",
        cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <Car className="h-4 w-4 text-muted-foreground" />
            <span className="font-mono font-medium">
              {row.original.exit_plate}
            </span>
            {row.original.is_fuzzy &&
              row.original.entry_plate !== row.original.exit_plate && (
                <Badge variant="secondary" className="text-xs ml-2">
                  Changed
                </Badge>
              )}
          </div>
        ),
      },
      {
        id: "exit_time",
        header: "Exit Time",
        accessorKey: "exit_time",
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-medium">
              {row.original.exit_time
                ? formatDateTime(row.original.exit_time)
                : "Active"}
            </div>
            {row.original.exit_time && (
              <div className="text-xs text-muted-foreground">
                Cam {row.original.exit_camera}
              </div>
            )}
          </div>
        ),
      },
      {
        id: "duration",
        header: "Duration",
        accessorKey: "duration_minutes",
        cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {row.original.duration_minutes != null
                ? formatDuration(row.original.duration_minutes)
                : "Active"}
            </span>
          </div>
        ),
      },
      {
        id: "location",
        header: "Zone",
        accessorKey: "location",
        cell: ({ row }) => (
          <Badge variant="outline">{row.original.location}</Badge>
        ),
      },
      {
        id: "entry_state",
        header: "Entry State",
        accessorKey: "entry_state",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.entry_state || "N/A"}</span>
        ),
      },
      {
        id: "exit_state",
        header: "Exit State",
        accessorKey: "exit_state",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.exit_state || "N/A"}</span>
        ),
      },
      {
        id: "match_type",
        header: "Match Type",
        accessorKey: "match_type",
        cell: ({ row }) => {
          const isFuzzy = row.original.is_fuzzy;
          const matchType = row.original.match_type || "exact";
          return (
            <Badge variant={isFuzzy ? "secondary" : "default"}>
              {isFuzzy ? "Fuzzy" : "Exact"}{" "}
              {matchType !== "exact" && matchType !== "fuzzy"
                ? `(${matchType})`
                : ""}
            </Badge>
          );
        },
      },
      {
        id: "fuzzy_distance",
        header: "Fuzzy Dist",
        accessorKey: "fuzzy_distance",
        cell: ({ row }) => {
          if (!row.original.is_fuzzy || !row.original.fuzzy_distance)
            return <span className="text-muted-foreground">-</span>;
          return (
            <Badge variant="outline" className="text-xs">
              {row.original.fuzzy_distance} chars
            </Badge>
          );
        },
      },
      {
        id: "state_mismatch",
        header: "State Match",
        accessorKey: "state_mismatch",
        cell: ({ row }) => {
          const mismatch = row.original.state_mismatch;
          if (mismatch === null || mismatch === undefined)
            return <span className="text-muted-foreground">-</span>;
          return (
            <Badge
              variant={mismatch ? "destructive" : "default"}
              className="text-xs"
            >
              {mismatch ? "Mismatch" : "Match"}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => handleViewSession(row.original.id)}
              >
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleEditSession(row.original.id)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit Session
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDeleteSession(row.original.id)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Session
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [],
  );

  const handleViewSession = (sessionId: string) => {
    // TODO: Navigate to session detail page
    console.log("View session:", sessionId);
  };

  const handleEditSession = (sessionId: string) => {
    // TODO: Open edit dialog
    console.log("Edit session:", sessionId);
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      await sessionAPI.deleteSession(sessionId);
      success("Session deleted successfully");
      refresh();
    } catch (err: any) {
      showError("Delete failed", err.message);
    }
  };

  const handleExport = () => {
    try {
      if (!sessions || sessions.length === 0) {
        showError(
          "No data to export",
          "There are no sessions in the current view",
        );
        return;
      }

      const filename = generateCSVFilename("sessions");
      exportSessionsToCSV(sessions, filename);
      success("Export complete", `Downloaded ${sessions.length} sessions`);
    } catch (err: any) {
      showError("Export failed", err.message || "Failed to export data");
    }
  };

  const handleFilterChange = (key: keyof SessionFilters, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    updateParams(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    updateParams({});
  };

  // Get active filter labels for display
  const getFilterLabel = () => {
    if (filters.is_fuzzy === "true") return "Fuzzy Matches";
    if (filters.is_fuzzy === "false") return "Exact Matches";
    if (filters.state_mismatch === "true") return "State Mismatches";
    if (filters.duration_type === "same_day") return "Same-Day Sessions";
    if (filters.duration_type === "overnight") return "Overnight Sessions";
    if (filters.duration_type === "multiday") return "Multi-Day Sessions";
    return null;
  };

  const activeFilterLabel = getFilterLabel();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sessions</h1>
          <p className="text-muted-foreground">
            View and manage all parking sessions
            {activeFilterLabel && (
              <Badge variant="secondary" className="ml-2">
                Filtered: {activeFilterLabel}
              </Badge>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {activeFilterLabel === "Fuzzy Matches" &&
            sessions &&
            sessions.length > 0 && (
              <FuzzyMatchingInfoModal
                entryPlate={sessions[0].entry_plate}
                exitPlate={sessions[0].exit_plate}
              />
            )}
          <Button variant="outline" onClick={clearFilters}>
            Clear Filters
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Global Filters */}
      <GlobalFilterBar />

      {/* Additional Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Additional Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="text-sm font-medium mb-1 block">
                License Plate
              </label>
              <Input
                placeholder="Search plates..."
                value={filters.license_plate || ""}
                onChange={(e) =>
                  handleFilterChange("license_plate", e.target.value)
                }
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Status</label>
              <Select
                value={filters.status || "all"}
                onValueChange={(value) =>
                  handleFilterChange(
                    "status",
                    value === "all" ? undefined : value,
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="orphaned">Orphaned</SelectItem>
                  <SelectItem value="invalid">Invalid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Location</label>
              <Select
                value={filters.location || "all"}
                onValueChange={(value) =>
                  handleFilterChange(
                    "location",
                    value === "all" ? undefined : value,
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">State</label>
              <Select
                value={filters.state || "all"}
                onValueChange={(value) =>
                  handleFilterChange(
                    "state",
                    value === "all" ? undefined : value,
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-6">
          <ServerPaginatedTable
            columns={columns}
            data={sessions || []}
            pagination={pagination}
            onPageChange={(page) => {
              setCurrentPage(page);
              updateParams({
                page,
                per_page: pageSize,
                start_date: formatDateForAPI(dateRange.from),
                end_date: formatDateForAPI(dateRange.to),
                ...(selectedLocations.length > 0 && {
                  location: selectedLocations.join(","),
                }),
                ...filters,
              });
            }}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setCurrentPage(1);
              updateParams({
                page: 1,
                per_page: newPageSize,
                start_date: formatDateForAPI(dateRange.from),
                end_date: formatDateForAPI(dateRange.to),
                ...(selectedLocations.length > 0 && {
                  location: selectedLocations.join(","),
                }),
                ...filters,
              });
            }}
            loading={isLoading}
          />
        </CardContent>
      </Card>

      {error && (
        <div className="text-center text-destructive">
          Error loading sessions: {error.message}
        </div>
      )}
    </div>
  );
}
